import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { fetchAllLostItemLogs, createNoteByAdmin, updateNoteByAdmin, createNoteByAdminForFound, updateNoteByAdminForFound, fetchAllFoundItemLogs } from "./activityLogAsyncThunk";
import { RequestResults } from "../../common/RequestStatus";
import { LostItemLogsData } from "../../../types/ActivityLog";

interface ActivityLogState {
  data: LostItemLogsData[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: ActivityLogState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const ActivityLogSlice = createSlice({
  name: "activityLog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch LostItemLogs
    builder
      .addCase(fetchAllLostItemLogs.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchAllLostItemLogs.fulfilled, (state, action: PayloadAction<LostItemLogsData[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchAllLostItemLogs.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch notes.");
        state.error = action.payload || "Failed to fetch notes.";
      });

    // Add Note
    builder
      .addCase(createNoteByAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNoteByAdmin.fulfilled, (state, action: PayloadAction<LostItemLogsData>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createNoteByAdmin.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add note.");
        state.error = action.payload || "Failed to add note.";
      });

    // Update Note
    builder
      .addCase(updateNoteByAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateNoteByAdmin.fulfilled, (state, action: PayloadAction<LostItemLogsData>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateNoteByAdmin.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update note.");
        state.error = action.payload || "Failed to update note.";
      });
      
      // Fetch FoundItemLogs
      builder
      .addCase(fetchAllFoundItemLogs.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchAllFoundItemLogs.fulfilled, (state, action: PayloadAction<LostItemLogsData[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchAllFoundItemLogs.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch notes.");
        state.error = action.payload || "Failed to fetch notes.";
      });

    // Add Note For Found
    builder
      .addCase(createNoteByAdminForFound.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNoteByAdminForFound.fulfilled, (state, action: PayloadAction<LostItemLogsData>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createNoteByAdminForFound.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add note for found.");
        state.error = action.payload || "Failed to add note for found.";
      });

    // Update Note For Found
    builder
      .addCase(updateNoteByAdminForFound.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateNoteByAdminForFound.fulfilled, (state, action: PayloadAction<LostItemLogsData>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateNoteByAdminForFound.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update note for found.");
        state.error = action.payload || "Failed to update note for found.";
      });
  },
});

export const selectActivityLog = (state: RootState) => state.activityLog;

export default ActivityLogSlice.reducer;
